//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { ScheduledPostTimeUpdate } from '@bvengineering/universal-scheduling-gateway-service-types';
import moment from 'moment-timezone';
import { PojoMap } from 'pojo-maps';

import {
  isInstagramFeedAutomaticPostConfig,
  isInstagramFeedManualPostConfig,
  isInstagramReelsAutomaticPostConfig,
  isInstagramReelsManualPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  instagramPostingGroupNames,
  instagramStoryPostingGroupNames,
} from '@apis/universal-posts/universalPostsApiModel';
import {
  getIsPosted,
  getStandardPostDetails,
  getThumbnailOffsetSeconds,
  getVideoUrl,
  isReelsPost,
  isUniversalPostItem,
} from '@apis/universal-posts/universalPostsUtils';
import DateString from '@models/DateString';
import {
  IInstagramPost,
  getMediaOverlayIconType as getLiveMediaOverlayIconType,
} from '@models/IInstagramMediaItem';
import {
  IMlsMediaItem,
  WithFullMediaAndNetworkItem,
  hasFullMediaAndNetworkItem,
  hasVideo,
} from '@models/IMlsMediaItem';
import { ExistsWithNetworkItem } from '@models/INetworkItem';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { queryClient } from '@queries/ReactQueryProvider';
import {
  FEED_PREVIEW_NUM_GRID_ITEMS,
  SEARCH_DAYS_LIMIT,
} from '@universal-scheduling/components/calendar/feed-preview/scheduleFeedPreviewConstants';
import {
  FeedPreviewMediaOverlayIconType,
  IFeedPreviewItem,
  IScheduledFeedPreviewItem,
} from '@universal-scheduling/components/calendar/feed-preview/scheduleFeedPreviewModel';
import { getIsAutomaticPostType } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import {
  NormalizedInstagramLivePosts,
  NormalizedScheduledPosts,
} from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { difference, move } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { getMediaMedsSizedImageUrl, getMedsSizedImageUrl } from '@utils/meds';
import { Normalized } from '@utils/normalize';
import { sortBy } from '@utils/sortBy';
import { truncateToMultiple } from '@utils/truncateToMultiple';

/**
 * The feed preview should always show all scheduled content with a
 * scheduled time of SEARCH_DAYS_LIMIT prior to today or later. This minimum
 * date is used to make sure that we fetch scheduled data at least as
 * far back as this when we refresh the posts, and that we don't show
 * any content scheduled prior to this in the preview.
 */
export const getFeedPreviewSearchStartDate = (timezone: TimeZoneId) =>
  new Date(
    DateString.addDays(DateString.getToday(timezone), -SEARCH_DAYS_LIMIT),
  );

export const instagramFeedPreviewScheduledPostingGroupNames = difference(
  instagramPostingGroupNames,
  instagramStoryPostingGroupNames,
);

/**
 * Determines if a scheduled post should be shown in the feed preview. We do not
 * show posts scheduled earlier than 2 weeks prior to today, IG story posts, IG
 * reels posts that aren't shared to feed, or posted, failed, or initiated feed posts.
 * We don't need to check for story posts or status posted because that's handled in the fetch request.
 */
export const shouldShowInFeedPreview = (
  post: IScheduledPost,
  timezone: TimeZoneId,
) => {
  const cutoffDate = getFeedPreviewSearchStartDate(timezone);
  const isPostPriorToCutoff = moment(post.scheduledTime).isBefore(
    moment(cutoffDate),
  );
  return !(
    isPostPriorToCutoff ||
    ((isInstagramReelsAutomaticPostConfig(post.config) ||
      isInstagramReelsManualPostConfig(post.config)) &&
      !post.config.shareToFeed) ||
    post.customStatus === UniversalPostableCustomStatus.PostInitiated ||
    post.status === PostableStatus.Failed
  );
};

export const getFeedPreviewMediaIconType = (
  post: WithFullMediaAndNetworkItem<IScheduledPost>,
  mediaItem: ExistsWithNetworkItem<IMlsMediaItem>,
): FeedPreviewMediaOverlayIconType => {
  const isGallery =
    (isInstagramFeedAutomaticPostConfig(post.config) ||
      isInstagramFeedManualPostConfig(post.config)) &&
    post.config.cuids.length > 1;

  if (isReelsPost(post.postType)) {
    return 'Reels';
  }
  if (isGallery) {
    return 'gallery';
  }
  if (hasVideo(mediaItem)) {
    return 'video';
  }

  return 'image';
};

export const getScheduledFeedPreviewItems = (
  posts: IScheduledPost[],
  timezone: TimeZoneId,
) => {
  return posts.reduce<IFeedPreviewItem[]>((previewItems, post) => {
    if (
      !hasFullMediaAndNetworkItem(post) ||
      !shouldShowInFeedPreview(post, timezone)
    ) {
      return previewItems;
    }

    const { config, mediaItems } = post;
    const primaryCuid = config.cuids[0];
    const previewMediaItem = primaryCuid
      ? PojoMap.get(mediaItems, primaryCuid)
      : PojoMap.values(mediaItems).pop();

    const thumbnailOffsetInSeconds = isUniversalPostItem(post)
      ? getThumbnailOffsetSeconds(post)
      : undefined;
    const { coverFileId } = getStandardPostDetails(post);

    if (isDefined(primaryCuid) && isDefined(previewMediaItem)) {
      const videoUrl = getVideoUrl(previewMediaItem);

      previewItems.push({
        cuid: primaryCuid,
        imageUrl: getMediaMedsSizedImageUrl(previewMediaItem, 'm'),
        shouldAppearPosted: getIsPosted(post),
        mediaType: getFeedPreviewMediaIconType(post, previewMediaItem),
        postId: post.id,
        scheduledTime: post.scheduledTime,
        postType: post.postType,
        ...(isDefined(videoUrl) && {
          videoInfo: {
            thumbnailOffsetInSeconds,
            videoUrl,
            coverImageUrl: isDefined(coverFileId)
              ? getMedsSizedImageUrl(coverFileId, 'm')
              : undefined,
          },
        }),
      });
    }

    return previewItems;
  }, []);
};

export const getInstagramLiveFeedPreviewItems = (posts: IInstagramPost[]) => {
  return posts.map((post) => {
    const item: IFeedPreviewItem = {
      cuid: post.cuid,
      imageUrl: post.media.image?.url ?? '',
      mediaType: getLiveMediaOverlayIconType(post),
      postedTime: post.postedTime,
    };
    return item;
  });
};

// TODO PD-260039: Handle TikTok
export const getInstagramFeedPreviewItems = (
  scheduledPosts: NormalizedScheduledPosts,
  livePosts: NormalizedInstagramLivePosts,
  timezone: TimeZoneId,
) => {
  const orderedScheduledPosts = Normalized.getOrdered(scheduledPosts);
  const orderedLivePosts = Normalized.getOrdered(livePosts);

  const scheduledItems = getScheduledFeedPreviewItems(
    orderedScheduledPosts,
    timezone,
  );

  const liveItems = getInstagramLiveFeedPreviewItems(orderedLivePosts);

  return getFilteredFeedPreviewItems(scheduledItems, liveItems);
};

export const getFilteredFeedPreviewItems = (
  scheduledItems: IFeedPreviewItem[],
  liveItems: IFeedPreviewItem[],
) => {
  const reversedScheduledItems = [...scheduledItems].reverse();

  // Filter out any past due posts that were scheduled to be posted before
  // the last live post because the user would not be able to post this
  // item in that order
  const mostRecentLiveItem = liveItems[0];
  const mostRecentLiveItemPostedTime = mostRecentLiveItem?.postedTime ?? 0;
  const reversedScheduledItemsFiltered = reversedScheduledItems.filter(
    (scheduledItem) => {
      const { scheduledTime } = scheduledItem;
      return (
        isDefined(scheduledTime) &&
        scheduledTime.getTime() > mostRecentLiveItemPostedTime
      );
    },
  );

  // We round to multiples of the number of grid items to show a neat feed preview grid.
  return truncateToMultiple(
    [...reversedScheduledItemsFiltered, ...liveItems],
    FEED_PREVIEW_NUM_GRID_ITEMS,
  );
};

/**
 * Creates a temporary copy of the current scheduled posts with the
 * post being rescheduled swapped out for a version with the new post time.
 * Resorts the list by scheduledTime so that it feeds through the data
 * selectors properly.
 */
export const getTemporaryScheduledPosts = (
  scheduledPosts: NormalizedScheduledPosts,
  postId: PostableId,
  newScheduledTime: Date,
): NormalizedScheduledPosts => {
  const rescheduledPost = Normalized.getById(scheduledPosts, postId);

  if (!rescheduledPost) {
    return scheduledPosts;
  }

  const byId = PojoMap.set(scheduledPosts.byId, postId, {
    ...rescheduledPost,
    scheduledTime: newScheduledTime,
  });

  const orderedObjects = PojoMap.values(byId);
  const orderedIds = sortBy(orderedObjects, 'scheduledTime').map((p) => p.id);

  return { byId, orderedIds };
};

export const invalidateFeedPreviewScheduledInstagramPostsQuery = (
  timezone: TimeZoneId,
) => {
  const earliestDate = getFeedPreviewSearchStartDate(timezone);

  void queryClient.invalidateQueries({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetFeedPreviewInstagramScheduledPosts,
      earliestDate,
    ],
  });
};

/**
 * Creates a temporary copy of current scheduled posts with scheduled times
 * updated for a batch reschedule. Re-sorts the list by scheduledTime so that
 * it feeds through the data selectors properly.
 *
 * @param scheduledPosts - Normalized scheduled posts
 * @param updates - List of postIds to reschedule and new scheduled times
 */
export const getTemporaryScheduledPostsForBatchReschedule = (
  scheduledPosts: NormalizedScheduledPosts,
  updates: ScheduledPostTimeUpdate[],
): NormalizedScheduledPosts => {
  return updates.reduce<NormalizedScheduledPosts>((posts, update) => {
    const updatePostableId = PostableId(update.postableId);
    const post = Normalized.getById(posts, updatePostableId);

    if (!isDefined(post)) {
      return posts;
    }

    return getTemporaryScheduledPosts(
      posts,
      updatePostableId,
      new Date(update.scheduledTime),
    );
  }, scheduledPosts);
};

export const getScheduledPostTimeUpdates = (
  feedPreviewItems: IScheduledFeedPreviewItem[],
  dragIndex: number,
  dropIndex: number,
) => {
  if (
    !isDefined(feedPreviewItems[dragIndex]) ||
    !isDefined(feedPreviewItems[dropIndex])
  ) {
    return;
  }

  // Get slice of items that have been reordered
  const itemsToUpdate = feedPreviewItems.slice(
    Math.min(dragIndex, dropIndex),
    Math.max(dragIndex, dropIndex) + 1,
  );

  const scheduledTimes = itemsToUpdate.map((item) => item.scheduledTime);

  // Check that none of the schedule times are in the past
  const currentTime = new Date().getTime();
  if (scheduledTimes.some((time) => time.getTime() <= currentTime)) {
    return;
  }

  const isDraggedItemFirst = dropIndex > dragIndex;

  // Reorder items within slice
  const reorderedItemIds = move(
    itemsToUpdate,
    isDraggedItemFirst ? 0 : itemsToUpdate.length - 1,
    isDraggedItemFirst ? itemsToUpdate.length - 1 : 0,
  ).map((item) => item.postId);

  // Create list of items to update with scheduled times shifted to match order
  const scheduledPostTimeUpdates: ScheduledPostTimeUpdate[] =
    scheduledTimes.reduce<ScheduledPostTimeUpdate[]>(
      (updates, scheduledTime, index) => {
        const postId = reorderedItemIds[index];

        if (isDefined(postId)) {
          updates.push({
            postableId: PostableId(postId),
            scheduledTime: scheduledTime.getTime(),
          });
        }

        return updates;
      },
      [],
    );

  if (scheduledPostTimeUpdates.length !== itemsToUpdate.length) {
    return;
  }

  return scheduledPostTimeUpdates;
};

/**
 * Check if scheduled feed preview item has a scheduled time in the past so we can disable
 * editing/drag and dropping that item
 * This includes processing complete items that won't show up in the live fetch yet
 */
export const isScheduledTimeInPast = (scheduledTime: Date) => {
  const currentTime = new Date().getTime();

  return currentTime >= scheduledTime.getTime();
};

/**
 * Check if scheduled feed preview item should show "past due" badge for IG manual posts.
 */
export const isManualAndPastDue = (item: IScheduledFeedPreviewItem) => {
  return (
    !getIsAutomaticPostType(item.postType) &&
    isScheduledTimeInPast(item.scheduledTime)
  );
};
